import { useState } from 'react';
import { Camera, CheckCircle2, RotateCcw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { PageHeader } from '../../components/Shared';
import ScanReceiptDialog from '../../components/ScanReceiptDialog';

/**
 * Employee-facing "scan a receipt" page. Engineers can snap the till receipt
 * at the supplier counter and have GPT-4o Vision parse it into a draft bill
 * for the admin to review — no paper bookkeeping ever.
 *
 * The same `<ScanReceiptDialog>` powers admin scans, but the flow here is
 * simplified: there's no inbox to navigate to (employees don't see the bills
 * inbox). On success we show a confirmation card with the parsed totals so
 * the engineer knows their submission is in the admin's queue.
 */
const EmployeeReceiptScan = () => {
  const [open, setOpen] = useState(false);
  const [lastParsed, setLastParsed] = useState(null);

  const reset = () => {
    setLastParsed(null);
    setOpen(true);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-2xl">
      <PageHeader
        title="Scan a receipt"
        description="Snap a photo of any supplier till receipt and we'll send it to the office for the books — no paperwork to bring back."
      />

      {!lastParsed ? (
        <div className="bg-white border border-slate-200 rounded-md p-6 sm:p-10 flex flex-col items-center text-center gap-4">
          <div className="h-16 w-16 rounded-full bg-amber-50 text-amber-700 flex items-center justify-center">
            <Camera className="h-8 w-8" />
          </div>
          <div className="space-y-1">
            <div className="font-display uppercase tracking-wide text-slate-900">Receipt scanner</div>
            <div className="text-sm text-slate-600 max-w-sm">
              JPEG, PNG, HEIC or PDF — up to 12&nbsp;MB. AI extracts the supplier, total, line items and VAT in seconds.
            </div>
          </div>
          <Button
            size="lg"
            onClick={() => setOpen(true)}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="employee-scan-receipt-btn"
          >
            <Camera className="h-5 w-5 mr-2" />
            Scan or upload receipt
          </Button>
          <ul className="text-xs text-slate-500 mt-2 space-y-1 text-left max-w-sm">
            <li>• Take the photo straight after paying — flatten the receipt and frame the whole thing.</li>
            <li>• You'll see the parsed total before submitting, so you can spot mistakes early.</li>
            <li>• The office sees it in the Bills Inbox the moment you submit, with your name attached.</li>
          </ul>
        </div>
      ) : (
        <div
          className="bg-emerald-50 border border-emerald-200 rounded-md p-6 sm:p-8 space-y-4"
          data-testid="employee-scan-success"
        >
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-8 w-8 text-emerald-700 shrink-0" />
            <div>
              <div className="font-display uppercase tracking-wide text-emerald-900">Sent for review</div>
              <div className="text-sm text-emerald-800">The office has been notified.</div>
            </div>
          </div>
          <div className="bg-white border border-emerald-200 rounded-md divide-y divide-emerald-100 text-sm">
            <Row label="Supplier" value={lastParsed.supplierName || 'Unknown'} />
            <Row label="Receipt #" value={lastParsed.invoiceNumber || '—'} />
            <Row label="Date" value={lastParsed.issueDate || '—'} />
            <Row label="Total" value={`£${(lastParsed.total || 0).toFixed(2)}`} bold />
            <Row label="Line items" value={lastParsed.lineItems?.length || 0} />
            <Row label="Paid by" value={(lastParsed.paymentMethod || 'on-account').replace('-', ' ')} />
          </div>
          <Button
            onClick={reset}
            variant="outline"
            className="w-full sm:w-auto"
            data-testid="employee-scan-another-btn"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Scan another receipt
          </Button>
        </div>
      )}

      <ScanReceiptDialog
        open={open}
        onOpenChange={setOpen}
        onParsed={(bill) => setLastParsed(bill)}
      />
    </div>
  );
};

const Row = ({ label, value, bold = false }) => (
  <div className="flex items-center justify-between px-4 py-3">
    <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`text-slate-900 capitalize ${bold ? 'font-display text-base tabular-nums' : ''}`}>{value}</div>
  </div>
);

export default EmployeeReceiptScan;
